import { type IntegrationSettings, type IntegrationSource } from "@prisma/client";
import { randomUUID } from "crypto";
import { createWriteStream, readFileSync } from "fs";
import imageSize from "image-size";
import { type Response } from "node-fetch";
import { tmpdir } from "os";
import { join } from "path";
import uniqid from "uniqid";
import { config } from "~/config";
import { type AppContext } from "~/lib/context";
import { BusinessLogicError } from "~/lib/errors/businessLogicError";
import { getPublicAssetFilePath } from "~/lib/files/getPublicAssetFilePath";
import { uploadFile } from "~/lib/files/uploadFile";
import { logError, logInfo } from "~/lib/logger";

export const getUploadedEmployeeProfilePicture = async (
  ctx: AppContext,
  options: {
    apiEmployeeId: string;
    source: IntegrationSource;
    fetch: () => Promise<Response>;
    integrationSettings: IntegrationSettings;
  }
) => {
  if (options.integrationSettings.anonymous) {
    logInfo(ctx, "[profile-picture] No picture to handle for employee", { options });

    return;
  }

  try {
    const picturePath = await getEmployeeProfilePicture(ctx, {
      apiEmployeeId: options.apiEmployeeId,
      source: options.source,
      fetch: options.fetch,
      integrationSettings: options.integrationSettings,
    });

    if (!picturePath) {
      return;
    }

    return uploadProfilePicture(ctx, { picturePath: picturePath });
  } catch (error) {
    logError(ctx, "[profile-picture] Error handling employee profile picture", {
      apiEmployeeId: options.apiEmployeeId,
      source: options.source,
      error,
    });
  }
};

const MIME_TYPE_EXTENSION_MAP: Record<string, string> = {
  "image/jpeg": ".jpg",
  "image/png": ".png",
  "image/gif": ".gif",
  "image/webp": ".webp",
  "image/svg+xml": ".svg",
  "image/bmp": ".bmp",
} as const;

const EXTENSION_MIME_TYPE_MAP: Record<string, string> = {
  ".jpg": "image/jpeg",
  ".png": "image/png",
  ".gif": "image/gif",
  ".webp": "image/webp",
  ".svg": "image/svg+xml",
  ".bmp": "image/bmp",
} as const;

const getEmployeeProfilePicture = async (
  ctx: AppContext,
  options: {
    apiEmployeeId: string;
    source: IntegrationSource;
    fetch: () => Promise<Response>;
    integrationSettings: IntegrationSettings;
  }
): Promise<string | null> => {
  if (options.integrationSettings.anonymous || config.dev.ignoreIntegrationProfilePics) {
    return null;
  }

  try {
    const res = await options.fetch();

    if (!res.ok) {
      throw new BusinessLogicError("Failed to fetch image").withMetadata({ res });
    }

    const contentType = res.headers.get("content-type") ?? "image/jpeg";
    const extension = MIME_TYPE_EXTENSION_MAP[contentType] ?? ".jpg";
    const path = join(tmpdir(), `${options.source}-image-${options.apiEmployeeId}-${randomUUID()}${extension}`);

    return new Promise((resolve, reject) => {
      const fileStream = createWriteStream(path);
      res.body.pipe(fileStream);
      res.body.on("error", (err) => reject(err));
      fileStream.on("finish", () => resolve(path));
    });
  } catch (error) {
    logError(ctx, "[profile-picture] There was an error when fetching an employee's profile picture.", {
      apiEmployeeId: options.apiEmployeeId,
      source: options.source,
      error,
    });

    return null;
  }
};

const uploadProfilePicture = async (ctx: AppContext, { picturePath }: { picturePath: string }) => {
  try {
    const fileBuffer = readFileSync(picturePath);
    const { width, height } = imageSize(fileBuffer);
    const extension = `.${picturePath.split(".").pop() ?? "jpg"}`;
    const mimeType = EXTENSION_MIME_TYPE_MAP[extension] ?? "image/jpeg";
    const path = `${config.files.prefixes.employeePictures}/${uniqid()}${extension}`;

    await uploadFile(ctx, {
      Bucket: config.files.bucket,
      Key: getPublicAssetFilePath(path),
      ContentType: mimeType,
      ContentLength: fileBuffer.length,
      Body: fileBuffer,
    });

    return { path, width, height, size: fileBuffer.length };
  } catch (error) {
    logError(ctx, "[profile-picture] Cannot upload employee profile picture to S3", {
      picturePath,
      error,
    });
  }
};
